import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useToast } from './Toast';
import { useConfirm } from './Modal';
import { useSocket } from '../contexts/SocketContext';
import { downloadPuzzleBatchExport } from '../utils/puzzleExport';
import { formatBJ } from '../utils/datetime';
import { useBeijingToday } from '../utils/useBeijingToday';
import { ImageViewer } from './PuzzleRecordsAdmin';
import { Puzzle, Download, Trash2, Loader, Inbox, Search } from 'lucide-react';

// 后台 · 证券图片管理（管理员全量 / 代理商名下）：
//   看图：已计入的成功记录，按日期 / 导出状态 / 关键词筛选，点开可看 最终图、字段图、主图、客户对话图、客户资料图
//   批量导出：默认只导还没导出过的最终图（压缩包里只有最终图）
//   清理：导出后不会自动删任何文件，在“已导出待清理”里手动确认（先清原始截图保留最终图，或连最终图一起清）
// 核实 / 解锁 / 更正字段 / 重新合成 在“识别记录”标签，不在这里。
const PAGE_SIZE = 24;
const EXP_FILTERS = [
  { key: 'all', label: '全部' },
  { key: 'none', label: '未导出' },
  { key: 'done', label: '已导出' },
  { key: 'cleaned', label: '已清理' },
];
const fmtBytes = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : b >= 1024 ? `${Math.round(b / 1024)} KB` : `${b || 0} B`);

export default function PuzzleImagesAdmin({ userId }) {
  const { showToast } = useToast();
  const confirm = useConfirm();
  const { subscribe } = useSocket();
  const [exp, setExp] = useState('all');
  // 日期：默认 = 今天（按提交当天），过了 0 点自动切到新的一天，不会把几天的记录混在一起。
  // dateSel：null = 跟随今天；'' = 全部日期；'YYYY-MM-DD' = 手动选的那天
  const today = useBeijingToday();
  const [dateSel, setDateSel] = useState(null);
  const date = dateSel === null ? today : dateSel;
  const [keyword, setKeyword] = useState('');
  const [kwInput, setKwInput] = useState('');
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({});
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [withExported, setWithExported] = useState(false);   // 包含已导出过的
  const [pending, setPending] = useState({ canCleanup: false, exports: [] });
  const [cleaningId, setCleaningId] = useState(null);

  const load = useCallback(async (pg, append) => {
    setLoading(true);
    try {
      const res = await api.get('/puzzle/records', {
        params: { status: 'counted', keyword, page: pg, pageSize: PAGE_SIZE, ...(exp !== 'all' ? { exp } : {}), ...(date ? { cdate: date } : {}), ...(userId ? { userId } : {}) },
      });
      setList(prev => (append ? [...prev, ...res.data.list] : res.data.list));
      setTotal(res.data.total);
      setSummary(res.data.summary || {});
      setPage(pg);
    } catch (err) {
      showToast(err.response?.data?.error || '加载失败', 'error');
    } finally { setLoading(false); }
  }, [exp, date, keyword, userId, showToast]);

  const loadPending = useCallback(async () => {
    try { setPending((await api.get('/puzzle/exports/pending')).data); } catch { /* 取不到就保留旧的 */ }
  }, []);

  useEffect(() => { load(1, false); }, [load]);
  useEffect(() => { loadPending(); }, [loadPending]);
  useEffect(() => {
    if (!subscribe) return;
    return subscribe((event) => { if (event === 'puzzle_updated') { load(1, false); loadPending(); } });
  }, [subscribe, load, loadPending]);

  const handleExport = async () => {
    setExporting(true);
    const r = await downloadPuzzleBatchExport({
      ...(userId ? { userId } : {}), ...(date ? { cdate: date } : {}),
      includeExported: withExported,
    });
    setExporting(false);
    if (r.ok) { showToast(`已导出 ${r.count ?? ''} 条，确认收好后可在下方清理释放空间`, 'success'); load(1, false); loadPending(); }
    else showToast(r.error, 'error');
  };

  // 清理：originals = 只删原始截图和字段图（最终图保留）；all = 连最终图一起删。导出后才会出现在这里，不会自动清理
  const handleCleanup = async (ex, level) => {
    const bytes = level === 'all' ? ex.bytes_all : ex.bytes_originals;
    const okc = await confirm(
      level === 'all' ? `确认把这批 ${ex.total_count} 条连最终图一起清理？` : `确认清理这批 ${ex.total_count} 条的原始截图和字段图？`,
      { detail: `预计释放 ${fmtBytes(bytes)}。${level === 'all' ? '最终图和不再需要的主图也会一并删除（每人当前正在用的主图不会删），之后网页里看不到这些图了，请先确认压缩包已保存好。' : '最终图会保留，网页里仍可查看；但清理后这些记录不能再更正字段、重新合成。'}`, danger: true });
    if (!okc) return;
    setCleaningId(ex.export_id + level);
    try {
      const res = await api.post(`/puzzle/export/${ex.export_id}/cleanup`, { level });
      showToast(res.data.alreadyCleaned ? '这一批已经清理过了' : `已清理 ${res.data.count} 条，释放 ${fmtBytes(res.data.freed)}`, 'success');
      loadPending(); load(1, false);
    } catch (e) { showToast(e.response?.data?.error || '清理失败', 'error'); }
    setCleaningId(null);
  };

  const exportable = withExported ? summary.counted : summary.exportable;

  return (
    <>
      <div className="card">
        <div style={{ display: 'flex', gap: 0 }}>
          {[['已计入', summary.counted, 'var(--c-success)'], ['未导出', summary.exportable, 'var(--c-primary)'], ['已导出', summary.exported, 'var(--c-text-secondary)'], ['已清理', summary.cleaned, '#b26a00']].map(([label, v, color], i) => (
            <React.Fragment key={label}>
              {i > 0 && <div style={{ width: 0.5, background: 'var(--c-divider)', margin: '10px 0' }} />}
              <div className="stat-card" style={{ flex: 1 }}>
                <div className="stat-value" style={{ color, fontSize: 22 }}>{v || 0}</div>
                <div className="stat-label">{label}</div>
              </div>
            </React.Fragment>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 13, color: 'var(--c-text-secondary)', flexWrap: 'wrap' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={withExported} onChange={e => setWithExported(e.target.checked)} /> 包含已导出过的
          </label>
        </div>
        <button className="btn btn-primary" onClick={handleExport} disabled={exporting || !exportable} style={{ marginTop: 10, height: 42 }}>
          {exporting ? <><Loader size={15} className="spin" /> 打包中，请稍候…</> : <><Download size={15} /> {withExported ? '导出全部已计入' : '导出还没导出过的'}{date ? `（${date}）` : ''}</>}
        </button>
        <div style={{ marginTop: 6, fontSize: 11, color: 'var(--c-text-tertiary)', lineHeight: 1.6 }}>
          {date ? `当前只导 ${date}${date === today ? '（今天）' : ''} 提交的；` : '当前是全部日期；'}导出不会删除任何图片，确认压缩包保存好后再在下方清理。
        </div>
      </div>

      {pending.canCleanup && pending.exports.length > 0 && (
        <div className="card">
          <div className="section-title" style={{ paddingTop: 4, display: 'flex', justifyContent: 'space-between' }}>
            <span>已导出待清理</span><span style={{ fontSize: 12, fontWeight: 500, color: 'var(--c-text-secondary)' }}>{pending.exports.length} 批 · {pending.totalRecords} 条</span>
          </div>
          {pending.exports.map(ex => (
            <div key={ex.export_id} style={{ padding: '10px 0', borderTop: '0.5px solid var(--c-divider)' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{formatBJ(ex.created_at)} · {ex.total_count} 条{ex.include_fields ? ' · 含字段图' : ''}</div>
              <div style={{ fontSize: 11, color: 'var(--c-text-tertiary)', marginTop: 2 }}>
                {ex.created_by_name ? `导出人 ${ex.created_by_name} · ` : ''}{ex.status === 'originals_cleaned' ? '原始截图已清理，最终图还在' : '文件都还在'}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                {ex.status !== 'originals_cleaned' && (
                  <button className="btn btn-outline btn-sm" disabled={!!cleaningId} onClick={() => handleCleanup(ex, 'originals')} style={{ flex: 1 }}>
                    {cleaningId === ex.export_id + 'originals' ? <Loader size={13} className="spin" /> : <Trash2 size={13} />} 清理原始截图（释放 {fmtBytes(ex.bytes_originals)}）
                  </button>
                )}
                <button className="btn btn-outline btn-sm" disabled={!!cleaningId} onClick={() => handleCleanup(ex, 'all')} style={{ flex: 1, color: 'var(--c-danger)' }}>
                  {cleaningId === ex.export_id + 'all' ? <Loader size={13} className="spin" /> : <Trash2 size={13} />} 全部清理（释放 {fmtBytes(ex.bytes_all)}）
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 10, overflowX: 'auto' }}>
        {EXP_FILTERS.map(f => (
          <button key={f.key} onClick={() => setExp(f.key)} style={{
            padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 600, flexShrink: 0, cursor: 'pointer',
            border: `1.5px solid ${exp === f.key ? 'var(--c-primary)' : 'var(--c-border)'}`,
            background: exp === f.key ? 'var(--c-primary)' : 'var(--c-surface)', color: exp === f.key ? '#fff' : 'var(--c-text-secondary)',
          }}>{f.label}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
          <Search size={14} color="var(--c-text-tertiary)" style={{ position: 'absolute', left: 12, top: 14 }} />
          <input className="input" style={{ paddingLeft: 34, fontSize: 16 }} placeholder="搜索号码 / 昵称 / 微信号 / 包名"
            value={kwInput} onChange={e => setKwInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') setKeyword(kwInput.trim()); }}
            onBlur={() => setKeyword(kwInput.trim())} />
        </div>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <input className="input" type="date" value={date} max={today} onChange={e => setDateSel(e.target.value === today ? null : e.target.value)} style={{ fontSize: 16, width: 150 }} />
        </div>
        {dateSel === null
          ? <button className="btn btn-outline btn-sm" onClick={() => setDateSel('')} style={{ flexShrink: 0, height: 44, fontSize: 12 }}>全部日期</button>
          : <button className="btn btn-outline btn-sm" onClick={() => setDateSel(null)} style={{ flexShrink: 0, height: 44, fontSize: 12 }}>回到今天</button>}
      </div>

      {list.length === 0 && !loading && (
        <div className="card empty-state" style={{ padding: '36px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}><Inbox size={40} color="var(--c-text-secondary)" strokeWidth={1.2} /></div>
          <p style={{ fontWeight: 600 }}>没有符合条件的图片</p>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {list.map(r => {
          const thumb = r.images?.combined || r.images?.chat;
          return (
            <div key={r.id} onClick={() => setViewing(r)} className="card" style={{ padding: 8, margin: 0, cursor: 'pointer', overflow: 'hidden' }}>
              <div style={{ height: 150, borderRadius: 8, overflow: 'hidden', background: 'var(--c-bg)', border: '1px solid var(--c-border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {thumb
                  ? <img src={thumb} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top left' }} />
                  : <div style={{ textAlign: 'center', color: 'var(--c-text-tertiary)', fontSize: 12 }}><Puzzle size={22} style={{ display: 'block', margin: '0 auto 4px' }} />{r.files_cleaned ? '图片已清理' : '暂无图片'}</div>}
              </div>
              <div style={{ fontSize: 14, fontWeight: 700, marginTop: 6, letterSpacing: 0.3 }}>{r.phone || '—'}</div>
              <div style={{ fontSize: 11, color: 'var(--c-text-secondary)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {r.user_name} · {r.package_name}{r.seq_no != null ? ` · 第${r.seq_no}单` : ''}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, fontSize: 11, color: 'var(--c-text-tertiary)' }}>
                <span>{formatBJ(r.confirmed_at || r.created_at)}</span>
                {r.files_cleaned ? <span className="badge" style={{ background: 'var(--c-bg)', color: 'var(--c-text-tertiary)' }}>已清理</span>
                  : r.exported && <span className="badge badge-info">已导出</span>}
              </div>
            </div>
          );
        })}
      </div>

      {loading && <p style={{ textAlign: 'center', padding: 16, color: 'var(--c-text-secondary)' }}>加载中...</p>}
      {!loading && list.length < total && (
        <button className="btn btn-outline" onClick={() => load(page + 1, true)} style={{ height: 42, marginTop: 10 }}>加载更多（{list.length} / {total}）</button>
      )}

      {viewing && <ImageViewer rec={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}
